import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, ApiError } from "../lib/api";
import { useActiveCategories, useMeta } from "../lib/meta";
import type { Card, CatalogMilestone, CreditLimitView, EarnRule, Person, RewardCurrency } from "../lib/types";
import { formatINR, formatDate, toDateInput } from "../lib/format";
import { baseRuleOf, describeMultiplier, describeRate, effectiveRate, formatMultiple, multipleOf, totalMultipleOf } from "../lib/rules";
import { Badge, EmptyState, ErrorBanner, Modal, PageHeader, Panel, Spinner } from "../components/ui";
import { BulkRuleEditor } from "../components/BulkRuleEditor";
import { ShareModal } from "../components/ShareModal";

export default function CardsPage() {
  const meta = useMeta();
  const activeCategories = useActiveCategories();
  const [cards, setCards] = useState<Card[] | null>(null);
  const [currencies, setCurrencies] = useState<RewardCurrency[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [sharing, setSharing] = useState<{ kind: "RULE"; sourceId: string; title: string } | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [view, setView] = useState<"cards" | "rules">("cards");

  const [templateOpen, setTemplateOpen] = useState(false);
  const [cardModal, setCardModal] = useState<{ open: boolean; card: Card | null }>({ open: false, card: null });
  const [ruleModal, setRuleModal] = useState<{ open: boolean; cardId: string; rule: EarnRule | null }>({
    open: false,
    cardId: "",
    rule: null,
  });

  const [credit, setCredit] = useState<CreditLimitView[]>([]);

  const load = useCallback(async () => {
    try {
      const [c, cur, cr] = await Promise.all([
        api.get<{ cards: Card[] }>("/cards"),
        api.get<{ currencies: RewardCurrency[] }>("/points/currencies"),
        api.get<{ limits: CreditLimitView[] }>("/cards/credit"),
      ]);
      setCards(c.cards);
      setCurrencies(cur.currencies);
      setCredit(cr.limits);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not load your cards");
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  async function removeCard(card: Card) {
    if (!confirm(`Delete ${card.name}? Its ${card._count?.transactions ?? 0} transactions will be deleted too.`)) return;
    try {
      await api.del(`/cards/${card.id}`);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not delete the card");
    }
  }

  async function removeRule(cardId: string, rule: EarnRule) {
    if (!confirm(`Delete the rule "${rule.name}"?`)) return;
    try {
      await api.del(`/cards/${cardId}/rules/${rule.id}`);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not delete the rule");
    }
  }

  if (!meta || !cards) return <Spinner />;

  return (
    <>
      <PageHeader
        title="Cards & earn rules"
        subtitle="Rules decide what each spend earns. Edit them to match your card's current terms."
        actions={
          <>
            <button className="btn-secondary" onClick={() => setCardModal({ open: true, card: null })}>
              Add manually
            </button>
            <button className="btn-primary" onClick={() => setTemplateOpen(true)}>Add from catalogue</button>
          </>
        }
      />

      {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}
      {notice && (
        <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
          {notice}
        </div>
      )}

      <div className="mb-6 flex gap-1 border-b border-ink-200">
        {([["cards", "Cards"], ["rules", "All earn rules"]] as const).map(([key, label]) => (
          <button
            key={key}
            onClick={() => { setView(key); setError(null); setNotice(null); }}
            className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
              view === key ? "border-accent-600 text-accent-800" : "border-transparent text-ink-500 hover:text-ink-800"
            }`}
            aria-current={view === key ? "page" : undefined}
          >
            {label}
          </button>
        ))}
      </div>

      {view === "rules" && (
        <Panel
          title="All earn rules"
          subtitle="Every rule across every card, so you can check the rates against your real card terms in one pass"
        >
          <BulkRuleEditor
            cards={cards}
            categories={meta.categories}
            onError={setError}
            onSaved={(n) => {
              setNotice(`Saved ${n} rule${n === 1 ? "" : "s"}. Run Recalculate points on the Transactions page to re-score your history.`);
              void load();
            }}
          />
        </Panel>
      )}

      {view === "cards" && (cards.length === 0 ? (
        <EmptyState
          title="No cards yet"
          hint="Start from the built-in catalogue of Indian cards — earn rules, caps and milestones come pre-filled and stay editable."
          action={<button className="btn-primary" onClick={() => setTemplateOpen(true)}>Browse the catalogue</button>}
        />
      ) : (
        <div className="space-y-4">
          {cards.map((card) => (
            <section key={card.id} className="card-surface overflow-hidden">
              <header className="flex flex-wrap items-start gap-4 border-b border-ink-200/70 p-5">
                <span className="h-12 w-2 shrink-0 rounded-full" style={{ background: card.color }} aria-hidden />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="text-base font-semibold text-ink-950">{card.name}</h2>
                    {!card.isActive && <Badge>Inactive</Badge>}
                    {card.last4 && <span className="text-xs text-ink-400">•••• {card.last4}</span>}
                  </div>
                  <p className="mt-0.5 text-sm text-ink-500">
                    {card.issuer} · {card.network} · {card.currency?.name ?? "No reward currency"}
                  </p>
                  <dl className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-xs text-ink-600">
                    <div>
                      <dt className="inline text-ink-400">Base rate: </dt>
                      <dd className="tnum inline">
                        {(() => {
                          const base = baseRuleOf(card.earnRules);
                          return base ? `${base.rate} pts / ₹${base.unitAmount}` : <span className="text-amber-700">not set</span>;
                        })()}
                      </dd>
                    </div>
                    <div><dt className="inline text-ink-400">Annual fee: </dt><dd className="tnum inline">{formatINR(card.annualFee)}</dd></div>
                    {card.feeWaiverSpend && (
                      <div><dt className="inline text-ink-400">Waived at: </dt><dd className="tnum inline">{formatINR(card.feeWaiverSpend)}</dd></div>
                    )}
                    <div><dt className="inline text-ink-400">Statement: </dt><dd className="tnum inline">day {card.statementDay}</dd></div>
                    <div><dt className="inline text-ink-400">Due: </dt><dd className="tnum inline">day {card.dueDay}</dd></div>
                    <div><dt className="inline text-ink-400">Forex: </dt><dd className="tnum inline">{card.forexMarkupPct}%</dd></div>
                    {card.anniversaryDate && (
                      <div><dt className="inline text-ink-400">Anniversary: </dt><dd className="inline">{formatDate(card.anniversaryDate)}</dd></div>
                    )}
                    <div><dt className="inline text-ink-400">Transactions: </dt><dd className="tnum inline">{card._count?.transactions ?? 0}</dd></div>
                    {(() => {
                      const limit = credit.find((l) => l.cards.some((c) => c.id === card.id));
                      if (!limit) return null;
                      const others = limit.cards.filter((c) => c.id !== card.id).map((c) => c.name);
                      return (
                        <div>
                          <dt className="inline text-ink-400">Available: </dt>
                          <dd className="tnum inline">
                            {formatINR(limit.available)} of {formatINR(limit.limit, { compact: limit.limit >= 100000 })}
                            {others.length > 0 && <span className="text-ink-500"> · shared with {others.join(", ")}</span>}
                          </dd>
                        </div>
                      );
                    })()}
                  </dl>
                  {!baseRuleOf(card.earnRules) && card.earnRules.some((r) => r.multiplier != null) && (
                    <p className="mt-2 rounded-md bg-amber-50 px-2.5 py-1.5 text-xs text-amber-800">
                      No base rate: rules set as a multiple of it (5X, 10X…) earn nothing on this card. Set it with Edit.
                    </p>
                  )}
                </div>
                <div className="flex shrink-0 gap-2">
                  <button className="btn-secondary px-3 py-1.5 text-xs" onClick={() => setCardModal({ open: true, card })}>
                    Edit
                  </button>
                  <button
                    className="btn-secondary px-3 py-1.5 text-xs"
                    onClick={() => setExpanded(expanded === card.id ? null : card.id)}
                    aria-expanded={expanded === card.id}
                  >
                    {expanded === card.id ? "Hide rules" : `${card.earnRules.length} rules`}
                  </button>
                  <button className="btn-danger px-3 py-1.5 text-xs" onClick={() => removeCard(card)}>Delete</button>
                </div>
              </header>

              {expanded === card.id && (
                <div className="p-5">
                  <div className="mb-3 flex items-center justify-between">
                    <p className="text-xs text-ink-500">
                      Highest priority matching rule wins. Exclusions override everything.
                    </p>
                    <button
                      className="btn-secondary px-3 py-1.5 text-xs"
                      onClick={() => setRuleModal({ open: true, cardId: card.id, rule: null })}
                    >
                      Add rule
                    </button>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-ink-200">
                          <th className="th">Rule</th>
                          <th className="th">Applies to</th>
                          <th className="th text-right">Rate</th>
                          <th className="th text-right">Cap</th>
                          <th className="th text-right">Priority</th>
                          <th className="th"><span className="sr-only">Actions</span></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-ink-100">
                        {card.earnRules.map((rule) => (
                          <tr key={rule.id}>
                            <td className="td">
                              <span className={`font-medium ${ruleState(rule) === "ENDED" ? "text-ink-400 line-through" : "text-ink-900"}`}>{rule.name}</span>
                              {rule.isExclusion && <Badge tone="critical">Earns nothing</Badge>}
                              {ruleState(rule) === "ENDED" && <Badge tone="neutral">Ended</Badge>}
                              {ruleState(rule) === "UPCOMING" && <Badge tone="info">Upcoming</Badge>}
                              {(rule.validFrom || rule.validUntil) && (
                                <span className="block text-xs text-ink-500">{validityLabel(rule)}</span>
                              )}
                              {rule.notes && <span className="block text-xs text-ink-500">{rule.notes}</span>}
                            </td>
                            <td className="td text-xs text-ink-600">
                              {describeScope(rule, meta.categories)}
                            </td>
                            <td className="td tnum whitespace-nowrap text-right">
                              {rule.isExclusion
                                ? "—"
                                : rule.multiplier != null
                                  ? describeMultiplier(rule)
                                  : `${rule.rate} / ₹${rule.unitAmount}`}
                              {!rule.isExclusion && rule.multiplier != null && (() => {
                                const { rate, unitAmount } = effectiveRate(rule, baseRuleOf(card.earnRules));
                                return <span className="block text-xs text-ink-500">= {rate} / ₹{unitAmount} today</span>;
                              })()}
                              {!rule.isExclusion && rule.multiplier == null && rule.id !== baseRuleOf(card.earnRules)?.id && (() => {
                                const m = multipleOf(rule, baseRuleOf(card.earnRules));
                                return m !== null && Math.abs(m - 1) > 1e-9 ? (
                                  <span className="block text-xs text-ink-500">{formatMultiple(m)} incl. base</span>
                                ) : null;
                              })()}
                            </td>
                            <td className="td tnum whitespace-nowrap text-right text-ink-600">
                              {rule.capType === "NONE" || rule.capValue == null ? (
                                "—"
                              ) : (
                                <>
                                  {`${rule.capType === "POINTS" ? formatPointsCap(rule.capValue) : formatINR(rule.capValue)} / ${rule.capPeriod.toLowerCase()}`}
                                  {(rule.capAppliesTo === "BONUS" || rule.capOverflow === "NOTHING" || rule.capGroup) && (
                                    <span className="block text-xs text-ink-500">
                                      {[
                                        rule.capAppliesTo === "BONUS" && "bonus only",
                                        rule.capAppliesTo !== "BONUS" && rule.capOverflow === "NOTHING" && "nothing past it",
                                        rule.capGroup && `shared: ${rule.capGroup}`,
                                      ]
                                        .filter(Boolean)
                                        .join(" · ")}
                                    </span>
                                  )}
                                </>
                              )}
                            </td>
                            <td className="td tnum text-right text-ink-500">{rule.priority}</td>
                            <td className="td whitespace-nowrap text-right">
                              <button
                                className="text-xs text-accent-700 underline"
                                onClick={() => setRuleModal({ open: true, cardId: card.id, rule })}
                              >
                                Edit
                              </button>
                              <button
                                className="ml-3 text-xs text-accent-700 underline"
                                onClick={() => setSharing({ kind: "RULE", sourceId: rule.id, title: `${rule.name} — ${card.name}` })}
                              >
                                Share
                              </button>
                              <button className="ml-3 text-xs text-red-700 underline" onClick={() => removeRule(card.id, rule)}>
                                Delete
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="mt-3 text-xs text-ink-500">
                    After changing rules, use <strong>Recalculate points</strong> on the Transactions page to re-score your history.
                  </p>
                </div>
              )}
            </section>
          ))}
        </div>
      ))}

      <TemplateModal
        open={templateOpen}
        catalog={meta.catalog}
        heldNames={cards.map((c) => c.name)}
        onClose={() => setTemplateOpen(false)}
        onAdded={() => { setTemplateOpen(false); void load(); }}
      />

      <CardModal
        state={cardModal}
        allCards={cards}
        currencies={currencies}
        networks={meta.networks}
        defaultForexMarkupPct={meta.preferences.defaultForexMarkupPct}
        onClose={() => setCardModal({ open: false, card: null })}
        onSaved={() => { setCardModal({ open: false, card: null }); void load(); }}
      />

      <ShareModal target={sharing} onClose={() => setSharing(null)} onShared={(m) => { setSharing(null); setNotice(m); }} />
      <RuleModal
        state={ruleModal}
        cardRules={cards.find((c) => c.id === ruleModal.cardId)?.earnRules ?? []}
        categories={activeCategories}
        capTypes={meta.capTypes}
        capPeriods={meta.capPeriods}
        onClose={() => setRuleModal({ open: false, cardId: "", rule: null })}
        onSaved={() => { setRuleModal({ open: false, cardId: "", rule: null }); void load(); }}
      />
    </>
  );
}

function formatPointsCap(value: number): string {
  return `${value.toLocaleString("en-IN")} pts`;
}

function describeScope(rule: EarnRule, categories: { key: string; label: string }[]): string {
  const parts: string[] = [];
  if (rule.categories.length > 0) {
    parts.push(rule.categories.map((k) => categories.find((c) => c.key === k)?.label ?? k).join(", "));
  }
  if (rule.merchantPatterns.length > 0) parts.push(`merchants: ${rule.merchantPatterns.join(", ")}`);
  if (rule.channel !== "ANY") parts.push(rule.channel === "ONLINE" ? "online only" : "in store only");
  if (rule.international === true) parts.push("international only");
  if (rule.international === false) parts.push("domestic only");
  if (rule.minAmount) parts.push(`min ${formatINR(rule.minAmount)}`);
  if ((rule.exceptPatterns ?? []).length > 0) parts.push(`except ${rule.exceptPatterns!.join(", ")}`);
  if (rule.qualifyingSpend) {
    const period = rule.qualifyingPeriod === "STATEMENT" ? "cycle" : (rule.qualifyingPeriod ?? "MONTH").toLowerCase();
    parts.push(
      rule.qualifyingMode === "ABOVE"
        ? `on spend above ${formatINR(rule.qualifyingSpend)} a ${period}`
        : `once a ${period}'s spend reaches ${formatINR(rule.qualifyingSpend)}`,
    );
  }
  if (rule.capIsCreditLimit) parts.push("up to the credit limit");
  if (rule.capPerDay) parts.push(`at most ${rule.capPerDay.toLocaleString("en-IN")} a day`);
  if (rule.subCapValue) parts.push(`own cap ${rule.subCapValue.toLocaleString("en-IN")}${rule.subCapGroup ? ` (${rule.subCapGroup})` : ""} within ${rule.capGroup || "its cap"}`);
  if (rule.bonusCreditDays != null) {
    // The same period the engine dates the credit from.
    const period = rule.qualifyingSpend ? rule.qualifyingPeriod : rule.capType !== "NONE" ? rule.capPeriod : "MONTH";
    const days = rule.bonusCreditWorkingDays ? "working days" : "days";
    parts.push(
      rule.bonusCreditBasis === "TRANSACTION"
        ? `bonus credited within ${rule.bonusCreditDays} ${days} of the spend`
        : period === "STATEMENT"
          ? rule.bonusCreditDays === 0
            ? "bonus credited with the statement"
            : `bonus credited ${rule.bonusCreditDays} ${days} after the statement`
          : `bonus credited within ${rule.bonusCreditDays} ${days} of the ${(period ?? "MONTH").toLowerCase()}'s end`,
    );
    if (rule.bonusEarlyMultiple && rule.bonusEarlyDays != null) {
      parts.push(`${rule.bonusEarlyMultiple}X of it within ${rule.bonusEarlyDays} ${days}`);
    }
  }
  if (rule.isExclusion && !rule.milestoneEligible) parts.push("not counted for milestones");
  return parts.length > 0 ? parts.join(" · ") : "Everything (base rate)";
}

/* ------------------------------------------------------------ catalogue */

const MILESTONE_PERIOD_LABEL: Record<string, string> = {
  CALENDAR_YEAR: "a calendar year",
  ANNIVERSARY_YEAR: "a card year",
  QUARTER: "a quarter",
  MONTH: "a month",
};

/**
 * A template's milestones in a line or two: "Milestones: ₹5L, ₹10L, ₹15L and
 * ₹20L a calendar year — ₹5,000 voucher each", one line per distinct reward.
 */
function describeMilestones(milestones: CatalogMilestone[], feeWaiverSpend: number | null): string[] {
  const groups = new Map<string, CatalogMilestone[]>();
  for (const m of milestones) {
    const key = `${m.period}|${m.rewardDescription}`;
    groups.set(key, [...(groups.get(key) ?? []), m]);
  }
  const lakhs = (n: number) => formatINR(n, { compact: n >= 100000 });
  const lines = [...groups.values()].map((group) => {
    const amounts = group.sort((a, b) => a.threshold - b.threshold).map((m) => lakhs(m.threshold));
    const list = amounts.length > 1 ? `${amounts.slice(0, -1).join(", ")} and ${amounts[amounts.length - 1]}` : amounts[0];
    const period = MILESTONE_PERIOD_LABEL[group[0].period] ?? "";
    return `Milestone${group.length > 1 ? "s" : ""}: ${list} ${period} — ${group[0].rewardDescription}${group.length > 1 ? " each" : ""}`;
  });
  if (feeWaiverSpend) lines.push(`Annual fee waived at ${lakhs(feeWaiverSpend)}`);
  return lines;
}

function TemplateModal({
  open,
  catalog,
  heldNames,
  onClose,
  onAdded,
}: {
  open: boolean;
  catalog: {
    key: string;
    name: string;
    issuer: string;
    annualFee: number;
    summary: string;
    feeWaiverSpend?: number | null;
    milestones?: CatalogMilestone[];
  }[];
  /** Names already on the account, so the catalogue can say what you hold. */
  heldNames: string[];
  onClose: () => void;
  onAdded: () => void;
}) {
  const [selected, setSelected] = useState<string | null>(null);
  const [statementDay, setStatementDay] = useState("1");
  const [dueDay, setDueDay] = useState("20");
  const [last4, setLast4] = useState("");
  const [anniversary, setAnniversary] = useState("");
  const [openingPoints, setOpeningPoints] = useState("0");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");

  const held = new Set(heldNames.map((n) => n.toLowerCase()));

  const filtered = catalog.filter(
    (c) =>
      c.name.toLowerCase().includes(search.toLowerCase()) ||
      c.issuer.toLowerCase().includes(search.toLowerCase()),
  );

  async function add() {
    if (!selected) return;
    setBusy(true);
    setError(null);
    try {
      await api.post("/cards/from-template", {
        key: selected,
        last4: last4 || undefined,
        statementDay: Number(statementDay),
        dueDay: Number(dueDay),
        anniversaryDate: anniversary || null,
        openingPoints: Number(openingPoints) || 0,
      });
      setSelected(null);
      setLast4("");
      setOpeningPoints("0");
      onAdded();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not add the card");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="Add a card from the catalogue" wide>
      <div className="space-y-4">
        <p className="text-xs text-ink-500">
          Rates and caps are sensible defaults as of the last update — issuers change them often, so check
          against your own terms and edit the rules after adding. The templates themselves are under{" "}
          <Link to="/settings?tab=templates" className="underline" onClick={onClose}>Settings → Templates</Link>.
        </p>

        <input
          className="input"
          placeholder="Search by card or issuer…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <div className="max-h-72 space-y-2 overflow-y-auto pr-1">
          {filtered.map((c) => {
            // Adding it again would be refused, so say so before the click
            // rather than after it.
            const alreadyHeld = held.has(c.name.toLowerCase());
            return (
              <button
                key={c.key}
                type="button"
                disabled={alreadyHeld}
                onClick={() => setSelected(c.key)}
                className={`w-full rounded-lg border p-3 text-left transition-colors ${
                  alreadyHeld
                    ? "cursor-not-allowed border-ink-200 bg-ink-50 opacity-60"
                    : selected === c.key
                      ? "border-accent-500 bg-accent-50"
                      : "border-ink-200 hover:bg-ink-50"
                }`}
              >
                <div className="flex items-baseline justify-between gap-3">
                  <span className="text-sm font-medium text-ink-900">
                    {c.name}
                    {alreadyHeld && <span className="ml-2 text-xs font-normal text-ink-500">already added</span>}
                  </span>
                  <span className="tnum shrink-0 text-xs text-ink-500">
                    {c.annualFee === 0 ? "Lifetime free" : `${formatINR(c.annualFee)} p.a.`}
                  </span>
                </div>
                <p className="mt-0.5 text-xs text-ink-500">{c.issuer} · {c.summary}</p>
                {describeMilestones(c.milestones ?? [], c.feeWaiverSpend ?? null).map((line) => (
                  <p key={line} className="mt-0.5 text-xs text-ink-600">{line}</p>
                ))}
              </button>
            );
          })}
          {filtered.length === 0 && <p className="py-6 text-center text-sm text-ink-500">No matching cards.</p>}
        </div>

        {selected && (
          <div className="grid gap-4 border-t border-ink-200 pt-4 sm:grid-cols-2">
            <div>
              <label className="label" htmlFor="c-last4">Last 4 digits (optional)</label>
              <input id="c-last4" className="input tnum" maxLength={4} value={last4} onChange={(e) => setLast4(e.target.value)} />
            </div>
            <div>
              <label className="label" htmlFor="c-anniv">Card anniversary (optional)</label>
              <input id="c-anniv" className="input" type="date" value={anniversary} onChange={(e) => setAnniversary(e.target.value)} />
            </div>
            <div>
              <label className="label" htmlFor="c-stmt">Statement day</label>
              <input id="c-stmt" className="input tnum" type="number" min={1} max={28} value={statementDay} onChange={(e) => setStatementDay(e.target.value)} />
            </div>
            <div>
              <label className="label" htmlFor="c-due">Payment due day</label>
              <input id="c-due" className="input tnum" type="number" min={1} max={28} value={dueDay} onChange={(e) => setDueDay(e.target.value)} />
            </div>
            <div className="sm:col-span-2">
              <label className="label" htmlFor="c-open">Points you already hold on this card</label>
              <input id="c-open" className="input tnum" type="number" min={0} value={openingPoints} onChange={(e) => setOpeningPoints(e.target.value)} />
            </div>
          </div>
        )}

        {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}

        <div className="flex justify-end gap-2">
          <button className="btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn-primary" onClick={add} disabled={!selected || busy}>
            {busy ? "Adding…" : "Add card"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

/* ------------------------------------------------------------ card form */

/** A milestone being written in the card form, before the card exists. Strings
 *  throughout: these are form fields, parsed once on save. */
type MilestoneDraft = {
  name: string;
  metric: string;
  threshold: string;
  minTransactionAmount: string;
  period: string;
  rewardDescription: string;
  rewardValue: string;
  rewardPoints: string;
};

const EMPTY_MILESTONE: MilestoneDraft = {
  name: "",
  metric: "SPEND",
  threshold: "",
  minTransactionAmount: "",
  period: "ANNIVERSARY_YEAR",
  rewardDescription: "",
  rewardValue: "",
  rewardPoints: "",
};

function CardModal({
  state,
  allCards,
  currencies,
  networks,
  defaultForexMarkupPct,
  onClose,
  onSaved,
}: {
  state: { open: boolean; card: Card | null };
  /** The account's cards, to show which share a limit with this one. */
  allCards: Card[];
  currencies: RewardCurrency[];
  networks: string[];
  defaultForexMarkupPct: number;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState({
    name: "", issuer: "", network: "VISA", last4: "", color: "#2a78d6", currencyId: "",
    statementDay: "1", dueDay: "20", annualFee: "0", joiningFee: "0", feeWaiverSpend: "",
    anniversaryDate: "", forexMarkupPct: "3.5", openingPoints: "0", openingPointsDate: "", creditLimit: "",
    sharesLimit: false,
    isActive: true, notes: "", personId: "", transferToOtherPersonAllowed: "",
    baseRate: "", baseUnit: "100",
  });
  const [addOns, setAddOns] = useState<{ last4: string; personId: string }[]>([]);
  const [newMilestones, setNewMilestones] = useState<MilestoneDraft[]>([]);
  const [people, setPeople] = useState<Person[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const c = state.card;
    setForm({
      name: c?.name ?? "",
      issuer: c?.issuer ?? "",
      network: c?.network ?? networks[0] ?? "VISA",
      last4: c?.last4 ?? "",
      color: c?.color ?? "#2a78d6",
      currencyId: c?.currencyId ?? "",
      statementDay: String(c?.statementDay ?? 1),
      dueDay: String(c?.dueDay ?? 20),
      annualFee: String(c?.annualFee ?? 0),
      joiningFee: String(c?.joiningFee ?? 0),
      feeWaiverSpend: c?.feeWaiverSpend != null ? String(c.feeWaiverSpend) : "",
      anniversaryDate: c?.anniversaryDate ? toDateInput(c.anniversaryDate) : "",
      forexMarkupPct: String(c?.forexMarkupPct ?? defaultForexMarkupPct),
      openingPoints: String(c?.openingPoints ?? 0),
      openingPointsDate: c?.openingPointsDate ? toDateInput(c.openingPointsDate) : "",
      creditLimit: c?.creditLimit != null ? String(c.creditLimit) : "",
      sharesLimit: c?.creditPoolId != null,
      isActive: c?.isActive ?? true,
      notes: c?.notes ?? "",
      personId: c?.personId ?? "",
      transferToOtherPersonAllowed:
        c?.transferToOtherPersonAllowed == null ? "" : String(c.transferToOtherPersonAllowed),
      ...(() => {
        const base = c ? baseRuleOf(c.earnRules) : null;
        return { baseRate: base ? String(base.rate) : "", baseUnit: String(base?.unitAmount ?? 100) };
      })(),
    });
    setAddOns((c?.addOns ?? []).map((a) => ({ last4: a.last4, personId: a.personId ?? "" })));
    setNewMilestones([]);
  }, [state.card, state.open]);

  useEffect(() => {
    if (!state.open) return;
    void api
      .get<{ people: Person[] }>("/people")
      .then((r) => setPeople(r.people))
      .catch(() => setPeople([]));
  }, [state.open]);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const payload = {
      name: form.name.trim(),
      issuer: form.issuer.trim(),
      network: form.network,
      last4: form.last4 || null,
      color: form.color,
      currencyId: form.currencyId || null,
      statementDay: Number(form.statementDay),
      dueDay: Number(form.dueDay),
      annualFee: Number(form.annualFee) || 0,
      joiningFee: Number(form.joiningFee) || 0,
      feeWaiverSpend: form.feeWaiverSpend ? Number(form.feeWaiverSpend) : null,
      anniversaryDate: form.anniversaryDate || null,
      forexMarkupPct: Number(form.forexMarkupPct) || 0,
      openingPoints: Number(form.openingPoints) || 0,
      openingPointsDate: form.openingPointsDate || null,
      creditLimit: form.creditLimit ? Number(form.creditLimit) : null,
      sharesLimit: form.sharesLimit && form.issuer.trim() !== "",
      isActive: form.isActive,
      notes: form.notes.trim() || null,
      personId: form.personId || null,
      transferToOtherPersonAllowed:
        form.transferToOtherPersonAllowed === "" ? null : form.transferToOtherPersonAllowed === "true",
      // Sent only when filled in: an empty box leaves the card's base rule as it is.
      ...(form.baseRate.trim() !== "" && Number(form.baseUnit) > 0
        ? { baseRate: { rate: Number(form.baseRate), unitAmount: Number(form.baseUnit) } }
        : {}),
      addOns: addOns.filter((a) => /^\d{4}$/.test(a.last4)).map((a) => ({ last4: a.last4, personId: a.personId || null })),
      addMilestones: newMilestones
        .filter((m) => m.name.trim() && m.rewardDescription.trim() && Number(m.threshold) > 0)
        .map((m) => ({
          name: m.name.trim(),
          metric: m.metric,
          threshold: Number(m.threshold),
          minTransactionAmount: m.minTransactionAmount ? Number(m.minTransactionAmount) : null,
          period: m.period,
          rewardDescription: m.rewardDescription.trim(),
          rewardValue: Number(m.rewardValue) || 0,
          rewardPoints: Number(m.rewardPoints) || 0,
        })),
    };
    try {
      if (state.card) await api.put(`/cards/${state.card.id}`, payload);
      else await api.post("/cards", payload);
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not save the card");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal open={state.open} onClose={onClose} title={state.card ? "Edit card" : "Add a card"} wide>
      <form onSubmit={save} className="space-y-4">
        {people.length > 0 && (
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="label" htmlFor="c-person">In whose name is this card?</label>
              <select
                id="c-person"
                className="input"
                value={form.personId}
                onChange={(e) => setForm({ ...form, personId: e.target.value })}
              >
                <option value="">Not assigned</option>
                {people.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}{p.relationship ? ` · ${p.relationship}` : ""}</option>
                ))}
              </select>
              <p className="mt-1 text-xs text-ink-500">
                The primary cardholder. Points earned on add-on cards belong here too.
              </p>
            </div>
            <div>
              <label className="label" htmlFor="c-cross">Transfers into someone else&apos;s loyalty account</label>
              <select
                id="c-cross"
                className="input"
                value={form.transferToOtherPersonAllowed}
                onChange={(e) => setForm({ ...form, transferToOtherPersonAllowed: e.target.value })}
              >
                <option value="">Follow the household setting</option>
                <option value="true">Allowed</option>
                <option value="false">Not allowed</option>
              </select>
              <p className="mt-1 text-xs text-ink-500">
                Most issuers only transfer into an account in the cardholder&apos;s own name.
              </p>
            </div>
          </div>
        )}

        <div>
          <label className="label">Add-on cards</label>
          <p className="mb-2 text-xs text-ink-500">
            A supplementary card on this account, carried by someone else. It has its own number, so
            listing it lets a charge slip find this card — and say whose spending it was. The points
            still belong to this account.
          </p>
          <div className="space-y-2">
            {addOns.map((a, i) => (
              <div key={i} className="flex items-center gap-2">
                <input
                  className="input w-28 tnum"
                  inputMode="numeric"
                  maxLength={4}
                  placeholder="1122"
                  aria-label={`Add-on card ${i + 1} last four digits`}
                  value={a.last4}
                  onChange={(e) =>
                    setAddOns(addOns.map((x, j) => (j === i ? { ...x, last4: e.target.value.replace(/\D/g, "").slice(0, 4) } : x)))
                  }
                />
                <select
                  className="input flex-1"
                  aria-label={`Who carries add-on card ${i + 1}`}
                  value={a.personId}
                  onChange={(e) => setAddOns(addOns.map((x, j) => (j === i ? { ...x, personId: e.target.value } : x)))}
                >
                  <option value="">Holder not recorded</option>
                  {people.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}{p.relationship ? ` · ${p.relationship}` : ""}</option>
                  ))}
                </select>
                <button
                  type="button"
                  className="text-xs text-red-700 underline"
                  onClick={() => setAddOns(addOns.filter((_, j) => j !== i))}
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
          <button
            type="button"
            className="btn-secondary mt-2 text-xs"
            onClick={() => setAddOns([...addOns, { last4: "", personId: "" }])}
          >
            Add an add-on card
          </button>
          {people.length === 0 && addOns.length > 0 && (
            <p className="mt-2 text-xs text-ink-500">
              Add the people in your household under <span className="font-medium">Settings → People</span> to
              name who carries each one.
            </p>
          )}
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="label" htmlFor="cf-name">Card name</label>
            <input id="cf-name" className="input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div>
            <label className="label" htmlFor="cf-issuer">Issuer</label>
            <input id="cf-issuer" className="input" required value={form.issuer} onChange={(e) => setForm({ ...form, issuer: e.target.value })} />
          </div>
          <div>
            <label className="label" htmlFor="cf-network">Network</label>
            <select id="cf-network" className="input" value={form.network} onChange={(e) => setForm({ ...form, network: e.target.value })}>
              {networks.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          <div>
            <label className="label" htmlFor="cf-currency">Reward currency</label>
            <select id="cf-currency" className="input" value={form.currencyId} onChange={(e) => setForm({ ...form, currencyId: e.target.value })}>
              <option value="">None</option>
              {currencies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div className="sm:col-span-2">
            <span className="label">Base rate</span>
            <div className="flex items-center gap-2">
              <input
                id="cf-base"
                aria-label="Base points earned"
                className="input tnum w-24"
                type="number"
                min={0}
                step="0.1"
                placeholder="5"
                value={form.baseRate}
                onChange={(e) => setForm({ ...form, baseRate: e.target.value })}
              />
              <span className="text-sm text-ink-600">points per ₹</span>
              <input
                aria-label="Per rupees spent"
                className="input tnum w-24"
                type="number"
                min={1}
                value={form.baseUnit}
                onChange={(e) => setForm({ ...form, baseUnit: e.target.value })}
              />
            </div>
            <p className="mt-1 text-xs text-ink-500">
              What every spend earns unless a rule says otherwise. Accelerated rules are then set as a multiple of this
              — 10X — and follow it if it changes.
            </p>
          </div>
          <div>
            <label className="label" htmlFor="cf-last4">Last 4 digits</label>
            <input id="cf-last4" className="input tnum" maxLength={4} value={form.last4} onChange={(e) => setForm({ ...form, last4: e.target.value })} />
          </div>
          <div>
            <label className="label" htmlFor="cf-color">Colour</label>
            <input id="cf-color" className="input h-10 p-1" type="color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} />
          </div>
          <div>
            <label className="label" htmlFor="cf-stmt">Statement day</label>
            <input id="cf-stmt" className="input tnum" type="number" min={1} max={28} value={form.statementDay} onChange={(e) => setForm({ ...form, statementDay: e.target.value })} />
          </div>
          <div>
            <label className="label" htmlFor="cf-due">Payment due day</label>
            <input id="cf-due" className="input tnum" type="number" min={1} max={28} value={form.dueDay} onChange={(e) => setForm({ ...form, dueDay: e.target.value })} />
          </div>
          <div>
            <label className="label" htmlFor="cf-fee">Annual fee (₹)</label>
            <input id="cf-fee" className="input tnum" type="number" min={0} value={form.annualFee} onChange={(e) => setForm({ ...form, annualFee: e.target.value })} />
          </div>
          <div>
            <label className="label" htmlFor="cf-waiver">Fee waived at spend (₹)</label>
            <input id="cf-waiver" className="input tnum" type="number" min={0} placeholder="No waiver" value={form.feeWaiverSpend} onChange={(e) => setForm({ ...form, feeWaiverSpend: e.target.value })} />
          </div>
          <div>
            <label className="label" htmlFor="cf-anniv">Card anniversary</label>
            <input id="cf-anniv" className="input" type="date" value={form.anniversaryDate} onChange={(e) => setForm({ ...form, anniversaryDate: e.target.value })} />
          </div>
          <div>
            <label className="label" htmlFor="cf-forex">Forex markup (%)</label>
            <input id="cf-forex" className="input tnum" type="number" min={0} step="0.1" value={form.forexMarkupPct} onChange={(e) => setForm({ ...form, forexMarkupPct: e.target.value })} />
          </div>
          <div>
            <label className="label" htmlFor="cf-open">Opening points balance</label>
            <input id="cf-open" className="input tnum" type="number" min={0} value={form.openingPoints} onChange={(e) => setForm({ ...form, openingPoints: e.target.value })} />
          </div>
          <div>
            <label className="label" htmlFor="cf-opendate">Opening balance dated</label>
            <input
              id="cf-opendate"
              className="input"
              type="date"
              value={form.openingPointsDate}
              onChange={(e) => setForm({ ...form, openingPointsDate: e.target.value })}
            />
            <p className="mt-1 text-xs text-ink-500">Used for expiry. Left blank, it counts as your oldest points.</p>
          </div>
          <div>
            <label className="label" htmlFor="cf-limit">Credit limit (₹)</label>
            <input id="cf-limit" className="input tnum" type="number" min={0} value={form.creditLimit} onChange={(e) => setForm({ ...form, creditLimit: e.target.value })} />
            {(() => {
              const issuer = form.issuer.trim();
              const sameBank = allCards.filter(
                (c) => c.id !== state.card?.id && c.issuer.toLowerCase() === issuer.toLowerCase(),
              );
              const sharing = sameBank.filter((c) => c.creditPoolId);
              return (
                <label className="mt-1.5 flex items-start gap-2 text-xs text-ink-600">
                  <input
                    type="checkbox"
                    className="mt-0.5 h-3.5 w-3.5 rounded border-ink-300 text-accent-600 focus:ring-accent-500"
                    checked={form.sharesLimit}
                    disabled={!issuer}
                    onChange={(e) => {
                      const on = e.target.checked;
                      // Joining a bank's shared limit shows its figure.
                      const pooled = sharing[0]?.creditLimit;
                      setForm({ ...form, sharesLimit: on, creditLimit: on && pooled != null && !form.creditLimit ? String(pooled) : form.creditLimit });
                    }}
                  />
                  <span>
                    Shared with my other {issuer || "same-bank"} cards
                    <span className="block text-ink-500">
                      {sharing.length > 0
                        ? `One limit with ${sharing.map((c) => c.name).join(", ")} — spend on any uses it up. Changing it here changes it for all.`
                        : sameBank.length > 0
                          ? `Tick this on ${sameBank.map((c) => c.name).join(", ")} too, and they will draw on one limit.`
                          : "For a bank that gives one limit across all your cards with it, like HDFC."}
                    </span>
                  </span>
                </label>
              );
            })()}
          </div>
        </div>

        <div className="rounded-lg border border-ink-200 p-3">
          <label className="label">Milestones</label>
          <p className="mb-2 text-xs text-ink-500">
            Spend thresholds that unlock a voucher or bonus points. Add them here and they start
            tracking as soon as the card is saved; the Milestones page edits them later. The annual
            fee waiver is not one of these — it comes from the fee figures above.
          </p>

          {(state.card?.milestones ?? []).filter((m) => m.type !== "FEE_WAIVER").length > 0 && (
            <ul className="mb-3 space-y-1 text-xs text-ink-600">
              {(state.card?.milestones ?? [])
                .filter((m) => m.type !== "FEE_WAIVER")
                .map((m) => (
                  <li key={m.id} className="flex justify-between gap-2 border-b border-ink-100 pb-1">
                    <span>{m.name}</span>
                    <span className="tnum text-ink-500">
                      {m.metric === "SPEND" ? `₹${m.threshold.toLocaleString("en-IN")}` : `${m.threshold} txns`}
                      {" · "}
                      {m.rewardDescription}
                    </span>
                  </li>
                ))}
            </ul>
          )}

          <div className="space-y-3">
            {newMilestones.map((m, i) => (
              <div key={i} className="rounded-md bg-ink-50 p-3">
                <div className="grid gap-2 sm:grid-cols-2">
                  <input
                    className="input"
                    placeholder="Name — e.g. ₹4L annual milestone"
                    aria-label={`Milestone ${i + 1} name`}
                    value={m.name}
                    onChange={(e) => setNewMilestones(newMilestones.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))}
                  />
                  <input
                    className="input"
                    placeholder="What you get — e.g. ₹5,000 voucher"
                    aria-label={`Milestone ${i + 1} reward`}
                    value={m.rewardDescription}
                    onChange={(e) => setNewMilestones(newMilestones.map((x, j) => (j === i ? { ...x, rewardDescription: e.target.value } : x)))}
                  />
                  <select
                    className="input"
                    aria-label={`Milestone ${i + 1} measured by`}
                    value={m.metric}
                    onChange={(e) => setNewMilestones(newMilestones.map((x, j) => (j === i ? { ...x, metric: e.target.value } : x)))}
                  >
                    <option value="SPEND">Amount spent</option>
                    <option value="TRANSACTION_COUNT">Number of transactions</option>
                  </select>
                  <input
                    className="input tnum"
                    type="number"
                    min={0}
                    placeholder={m.metric === "SPEND" ? "Threshold (₹)" : "How many transactions"}
                    aria-label={`Milestone ${i + 1} threshold`}
                    value={m.threshold}
                    onChange={(e) => setNewMilestones(newMilestones.map((x, j) => (j === i ? { ...x, threshold: e.target.value } : x)))}
                  />
                  <select
                    className="input"
                    aria-label={`Milestone ${i + 1} measured over`}
                    value={m.period}
                    onChange={(e) => setNewMilestones(newMilestones.map((x, j) => (j === i ? { ...x, period: e.target.value } : x)))}
                  >
                    <option value="ANNIVERSARY_YEAR">Card anniversary year</option>
                    <option value="CALENDAR_YEAR">Calendar year</option>
                    <option value="QUARTER">Calendar quarter</option>
                    <option value="MONTH">Calendar month</option>
                  </select>
                  <input
                    className="input tnum"
                    type="number"
                    min={0}
                    placeholder="Reward worth (₹)"
                    aria-label={`Milestone ${i + 1} reward value`}
                    value={m.rewardValue}
                    onChange={(e) => setNewMilestones(newMilestones.map((x, j) => (j === i ? { ...x, rewardValue: e.target.value } : x)))}
                  />
                  {m.metric === "TRANSACTION_COUNT" && (
                    <input
                      className="input tnum"
                      type="number"
                      min={0}
                      placeholder="Each spend at least (₹)"
                      aria-label={`Milestone ${i + 1} minimum transaction`}
                      value={m.minTransactionAmount}
                      onChange={(e) => setNewMilestones(newMilestones.map((x, j) => (j === i ? { ...x, minTransactionAmount: e.target.value } : x)))}
                    />
                  )}
                  <input
                    className="input tnum"
                    type="number"
                    min={0}
                    placeholder="Bonus points (optional)"
                    aria-label={`Milestone ${i + 1} bonus points`}
                    value={m.rewardPoints}
                    onChange={(e) => setNewMilestones(newMilestones.map((x, j) => (j === i ? { ...x, rewardPoints: e.target.value } : x)))}
                  />
                </div>
                <button
                  type="button"
                  className="mt-2 text-xs text-red-700 underline"
                  onClick={() => setNewMilestones(newMilestones.filter((_, j) => j !== i))}
                >
                  Remove
                </button>
              </div>
            ))}
          </div>

          <button
            type="button"
            className="btn-secondary mt-2 text-xs"
            onClick={() => setNewMilestones([...newMilestones, { ...EMPTY_MILESTONE }])}
          >
            Add a milestone
          </button>
        </div>

        <div>
          <label className="label" htmlFor="cf-notes">Notes</label>
          <input id="cf-notes" className="input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
        </div>

        <label className="flex items-center gap-2 text-sm text-ink-700">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-ink-300 text-accent-600 focus:ring-accent-500"
            checked={form.isActive}
            onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
          />
          Active — include this card in the optimiser
        </label>

        {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn-primary" disabled={busy}>{busy ? "Saving…" : "Save card"}</button>
        </div>
      </form>
    </Modal>
  );
}

/* ------------------------------------------------------------ rule form */

function RuleModal({
  state,
  cardRules,
  categories,
  capTypes,
  capPeriods,
  onClose,
  onSaved,
}: {
  state: { open: boolean; cardId: string; rule: EarnRule | null };
  /** The card's rules, for reading a rate against its base rate. */
  cardRules: EarnRule[];
  categories: { key: string; label: string }[];
  capTypes: string[];
  capPeriods: string[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState({
    name: "", priority: "0", categories: [] as string[], merchantPatterns: "",
    channel: "ANY", international: "ANY", rate: "1", unitAmount: "100", roundDown: true,
    minAmount: "", capType: "NONE", capValue: "", capPeriod: "MONTH", capMode: "BASE", capGroup: "",
    rateMode: "MULTIPLE" as "MULTIPLE" | "FIXED", multiplier: "", multiplierIncludesBase: true,
    isExclusion: false, milestoneEligible: true, notes: "",
    validFrom: "", validUntil: "",
    exceptPatterns: "", qualifyingSpend: "", qualifyingPeriod: "MONTH", qualifyingMode: "ALL",
    capIsCreditLimit: false, bonusCreditDays: "", subCapValue: "", subCapGroup: "",
    capPerDay: "", bonusCreditBasis: "PERIOD_END", bonusCreditWorkingDays: false, bonusEarlyMultiple: "", bonusEarlyDays: "",
  });
  // The card's base rate, for reading this rule's rate against — leaving out
  // the rule being edited, which may itself be the base.
  const base = baseRuleOf(cardRules.filter((r) => r.id !== state.rule?.id));
  // Only the rule the card actually uses as its base; a new rule with no
  // categories yet is still being set up as an accelerated one.
  const isBaseRule = state.rule != null && state.rule.id === baseRuleOf(cardRules)?.id;
  const multiplying = !isBaseRule && !form.isExclusion && form.rateMode === "MULTIPLE";
  /** Set to change the rule from a date, keeping the old terms before it. */
  const [changeFrom, setChangeFrom] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const r = state.rule;
    setForm({
      name: r?.name ?? "",
      priority: String(r?.priority ?? 10),
      categories: r?.categories ?? [],
      merchantPatterns: (r?.merchantPatterns ?? []).join(", "),
      channel: r?.channel ?? "ANY",
      international: r?.international == null ? "ANY" : r.international ? "INTL" : "DOMESTIC",
      rate: String(r?.rate ?? 1),
      unitAmount: String(r?.unitAmount ?? 100),
      roundDown: r?.roundDown ?? true,
      minAmount: r?.minAmount != null ? String(r.minAmount) : "",
      capType: r?.capType ?? "NONE",
      capValue: r?.capValue != null ? String(r.capValue) : "",
      capPeriod: r?.capPeriod ?? "MONTH",
      capMode: capModeOf(r),
      // New rules start as a multiple of the base; the base rule itself, and
      // any rule already at a fixed rate, stay fixed.
      rateMode:
        r?.multiplier != null
          ? "MULTIPLE"
          : r || !baseRuleOf(cardRules)
            ? "FIXED"
            : "MULTIPLE",
      multiplier: r?.multiplier != null ? String(r.multiplier) : "",
      multiplierIncludesBase: r?.multiplierIncludesBase ?? true,
      capGroup: r?.capGroup ?? "",
      isExclusion: r?.isExclusion ?? false,
      milestoneEligible: r?.milestoneEligible ?? true,
      notes: r?.notes ?? "",
      validFrom: r?.validFrom ? r.validFrom.slice(0, 10) : "",
      validUntil: r?.validUntil ? r.validUntil.slice(0, 10) : "",
      exceptPatterns: (r?.exceptPatterns ?? []).join(", "),
      qualifyingSpend: r?.qualifyingSpend != null ? String(r.qualifyingSpend) : "",
      qualifyingPeriod: r?.qualifyingPeriod ?? "MONTH",
      qualifyingMode: r?.qualifyingMode ?? "ALL",
      capIsCreditLimit: r?.capIsCreditLimit ?? false,
      bonusCreditDays: r?.bonusCreditDays != null ? String(r.bonusCreditDays) : "",
      subCapValue: r?.subCapValue != null ? String(r.subCapValue) : "",
      subCapGroup: r?.subCapGroup ?? "",
      capPerDay: r?.capPerDay != null ? String(r.capPerDay) : "",
      bonusCreditBasis: r?.bonusCreditBasis ?? "PERIOD_END",
      bonusCreditWorkingDays: r?.bonusCreditWorkingDays ?? false,
      bonusEarlyMultiple: r?.bonusEarlyMultiple != null ? String(r.bonusEarlyMultiple) : "",
      bonusEarlyDays: r?.bonusEarlyDays != null ? String(r.bonusEarlyDays) : "",
    });
    setChangeFrom(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.rule, state.open]);

  function toggleCategory(key: string) {
    setForm((f) => ({
      ...f,
      categories: f.categories.includes(key) ? f.categories.filter((k) => k !== key) : [...f.categories, key],
    }));
  }

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const payload = {
      name: form.name.trim(),
      priority: Number(form.priority) || 0,
      categories: form.categories,
      merchantPatterns: form.merchantPatterns.split(",").map((s) => s.trim()).filter(Boolean),
      channel: form.channel,
      international: form.international === "ANY" ? null : form.international === "INTL",
      ...(multiplying
        ? {
            // Stored for reference; the engine works the rate out from the base.
            multiplier: Number(form.multiplier),
            multiplierIncludesBase: form.multiplierIncludesBase,
            rate: base ? Math.round(base.rate * (totalMultipleOf({ multiplier: Number(form.multiplier), multiplierIncludesBase: form.multiplierIncludesBase }) ?? 0) * 100) / 100 : 0,
            unitAmount: base?.unitAmount ?? (Number(form.unitAmount) || 100),
          }
        : {
            multiplier: null,
            rate: Number(form.rate) || 0,
            unitAmount: Number(form.unitAmount) || 100,
          }),
      roundDown: form.roundDown,
      minAmount: form.minAmount ? Number(form.minAmount) : null,
      capType: form.capType,
      capValue: form.capValue ? Number(form.capValue) : null,
      capPeriod: form.capPeriod,
      capAppliesTo: form.capMode === "BONUS" ? "BONUS" : "ALL",
      capOverflow: form.capMode === "NOTHING" ? "NOTHING" : "BASE",
      capGroup: form.capGroup.trim() || null,
      isExclusion: form.isExclusion,
      milestoneEligible: form.milestoneEligible,
      notes: form.notes.trim() || null,
      validFrom: form.validFrom || null,
      validUntil: form.validUntil || null,
      exceptPatterns: form.exceptPatterns.split(",").map((s) => s.trim()).filter(Boolean),
      qualifyingSpend: !form.isExclusion && Number(form.qualifyingSpend) > 0 ? Number(form.qualifyingSpend) : null,
      qualifyingPeriod: form.qualifyingPeriod,
      qualifyingMode: form.qualifyingMode,
      capIsCreditLimit: form.capType === "SPEND" && form.capIsCreditLimit,
      bonusCreditDays: !form.isExclusion && form.bonusCreditDays.trim() !== "" ? Number(form.bonusCreditDays) : null,
      subCapValue: form.capType !== "NONE" && Number(form.subCapValue) > 0 ? Number(form.subCapValue) : null,
      subCapGroup: form.capType !== "NONE" ? form.subCapGroup.trim() || null : null,
      capPerDay: form.capType !== "NONE" && Number(form.capPerDay) > 0 ? Number(form.capPerDay) : null,
      bonusCreditBasis: form.bonusCreditBasis,
      bonusCreditWorkingDays: form.bonusCreditWorkingDays,
      bonusEarlyMultiple: Number(form.bonusEarlyMultiple) > 0 && form.bonusEarlyDays.trim() !== "" ? Number(form.bonusEarlyMultiple) : null,
      bonusEarlyDays: Number(form.bonusEarlyMultiple) > 0 && form.bonusEarlyDays.trim() !== "" ? Number(form.bonusEarlyDays) : null,
    };
    try {
      if (state.rule && changeFrom) {
        // The rule as it stands ends the day before; these terms start that day.
        await api.post(`/cards/${state.cardId}/rules/${state.rule.id}/change-from`, {
          from: changeFrom,
          rule: { ...payload, validFrom: null },
        });
      } else if (state.rule) {
        await api.put(`/cards/${state.cardId}/rules/${state.rule.id}`, payload);
      } else {
        await api.post(`/cards/${state.cardId}/rules`, payload);
      }
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not save the rule");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal open={state.open} onClose={onClose} title={state.rule ? "Edit earn rule" : "Add earn rule"} wide>
      <form onSubmit={save} className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="label" htmlFor="rf-name">Rule name</label>
            <input id="rf-name" className="input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. 5X on dining" />
          </div>
          <div>
            <label className="label" htmlFor="rf-priority">Priority (higher wins)</label>
            <input id="rf-priority" className="input tnum" type="number" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} />
          </div>
        </div>

        <label className="flex items-center gap-2 text-sm text-ink-700">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-ink-300 text-accent-600 focus:ring-accent-500"
            checked={form.isExclusion}
            onChange={(e) => setForm({ ...form, isExclusion: e.target.checked })}
          />
          This is an exclusion — matching spends earn nothing
        </label>

        {form.isExclusion && (
          <label className="flex items-center gap-2 pl-6 text-sm text-ink-700">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-ink-300 text-accent-600 focus:ring-accent-500"
              checked={form.milestoneEligible}
              onChange={(e) => setForm({ ...form, milestoneEligible: e.target.checked })}
            />
            …but it still counts toward milestones and the fee waiver
          </label>
        )}

        {!form.isExclusion && (
          <div className="rounded-lg border border-ink-200 p-3">
            {isBaseRule ? (
              <h3 className="label mb-2">The card's base rate</h3>
            ) : (
              <fieldset className="mb-3">
                <legend className="label mb-1">How it earns</legend>
                <div className="flex flex-wrap gap-4 text-sm text-ink-700">
                  <label className="flex items-center gap-2">
                    <input type="radio" name="rf-mode" checked={form.rateMode === "MULTIPLE"} onChange={() => setForm({ ...form, rateMode: "MULTIPLE" })} />
                    A multiple of the base rate
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="radio" name="rf-mode" checked={form.rateMode === "FIXED"} onChange={() => setForm({ ...form, rateMode: "FIXED" })} />
                    A fixed rate
                  </label>
                </div>
              </fieldset>
            )}

            {multiplying ? (
              <MultiplierFields
                multiplier={form.multiplier}
                includesBase={form.multiplierIncludesBase}
                base={base}
                onChange={(multiplier, includesBase) => setForm({ ...form, multiplier, multiplierIncludesBase: includesBase })}
              />
            ) : (
              <div className="grid gap-4 sm:grid-cols-3">
                <div>
                  <label className="label" htmlFor="rf-rate">
                    {isBaseRule ? "Points earned" : "Total points earned (base included)"}
                  </label>
                  <input id="rf-rate" className="input tnum" type="number" min={0} step="0.1" required value={form.rate} onChange={(e) => setForm({ ...form, rate: e.target.value })} />
                </div>
                <div>
                  <label className="label" htmlFor="rf-unit">Per ₹</label>
                  <input id="rf-unit" className="input tnum" type="number" min={1} required value={form.unitAmount} onChange={(e) => setForm({ ...form, unitAmount: e.target.value })} />
                </div>
                <div className="flex items-end pb-2">
                  <label className="flex items-center gap-2 text-sm text-ink-700">
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-ink-300 text-accent-600 focus:ring-accent-500"
                      checked={form.roundDown}
                      onChange={(e) => setForm({ ...form, roundDown: e.target.checked })}
                    />
                    Round spend down
                  </label>
                </div>
                <RateReading form={form} base={isBaseRule ? null : base} isBaseRule={isBaseRule} />
              </div>
            )}
          </div>
        )}

        <div>
          <span className="label">Categories (leave empty to apply to everything)</span>
          <div className="flex flex-wrap gap-2">
            {categories.map((c) => (
              <button
                key={c.key}
                type="button"
                onClick={() => toggleCategory(c.key)}
                className={`chip-action ring-1 ${
                  form.categories.includes(c.key)
                    ? "bg-accent-50 text-accent-800 ring-accent-200"
                    : "bg-white text-ink-600 ring-ink-200 hover:bg-ink-50"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="label" htmlFor="rf-merch">Merchant keywords (comma separated)</label>
            <input id="rf-merch" className="input" value={form.merchantPatterns} onChange={(e) => setForm({ ...form, merchantPatterns: e.target.value })} placeholder="amazon, swiggy, smartbuy" />
            <p className="mt-1 text-xs text-ink-500">Any one matches. Join words with + to need both: smartbuy+irctc.</p>
          </div>
          <div>
            <label className="label" htmlFor="rf-except">Except merchants (optional)</label>
            <input
              id="rf-except"
              className="input"
              value={form.exceptPatterns}
              onChange={(e) => setForm({ ...form, exceptPatterns: e.target.value })}
              placeholder="tax, gst"
            />
            <p className="mt-1 text-xs text-ink-500">
              Spends naming any of these are left out of this rule — government earns nothing, except tax payments.
            </p>
          </div>
          <div>
            <label className="label" htmlFor="rf-min">Minimum transaction (₹)</label>
            <input id="rf-min" className="input tnum" type="number" min={0} value={form.minAmount} onChange={(e) => setForm({ ...form, minAmount: e.target.value })} placeholder="Any amount" />
          </div>
          <div>
            <label className="label" htmlFor="rf-channel">Channel</label>
            <select id="rf-channel" className="input" value={form.channel} onChange={(e) => setForm({ ...form, channel: e.target.value })}>
              <option value="ANY">Online or in store</option>
              <option value="ONLINE">Online only</option>
              <option value="OFFLINE">In store only</option>
            </select>
          </div>
          <div>
            <label className="label" htmlFor="rf-intl">Domestic / international</label>
            <select id="rf-intl" className="input" value={form.international} onChange={(e) => setForm({ ...form, international: e.target.value })}>
              <option value="ANY">Both</option>
              <option value="DOMESTIC">Domestic only</option>
              <option value="INTL">International only</option>
            </select>
          </div>
        </div>

        {!form.isExclusion && (
          <div className="grid gap-4 sm:grid-cols-3">
            <div>
              <label className="label" htmlFor="rf-captype">Cap type</label>
              <select id="rf-captype" className="input" value={form.capType} onChange={(e) => setForm({ ...form, capType: e.target.value })}>
                {capTypes.map((t) => (
                  <option key={t} value={t}>
                    {t === "NONE" ? "No cap" : t === "POINTS" ? "Max points" : "Max spend"}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="label" htmlFor="rf-capval">Cap value</label>
              <input
                id="rf-capval"
                className="input tnum"
                type="number"
                min={0}
                disabled={form.capType === "NONE" || (form.capType === "SPEND" && form.capIsCreditLimit)}
                value={form.capType === "SPEND" && form.capIsCreditLimit ? "" : form.capValue}
                placeholder={form.capType === "SPEND" && form.capIsCreditLimit ? "Credit limit" : undefined}
                onChange={(e) => setForm({ ...form, capValue: e.target.value })}
              />
              {form.capType === "SPEND" && (
                <label className="mt-1.5 flex items-center gap-2 text-xs text-ink-600">
                  <input
                    type="checkbox"
                    className="h-3.5 w-3.5 rounded border-ink-300 text-accent-600 focus:ring-accent-500"
                    checked={form.capIsCreditLimit}
                    onChange={(e) => setForm({ ...form, capIsCreditLimit: e.target.checked })}
                  />
                  The card's credit limit
                </label>
              )}
            </div>
            <div>
              <label className="label" htmlFor="rf-capperiod">Resets every</label>
              <select
                id="rf-capperiod"
                className="input"
                disabled={form.capType === "NONE"}
                value={form.capPeriod}
                onChange={(e) => setForm({ ...form, capPeriod: e.target.value })}
              >
                {capPeriods.map((p) => (
                  <option key={p} value={p}>
                    {p === "STATEMENT" ? "Statement cycle" : p.charAt(0) + p.slice(1).toLowerCase()}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        {!form.isExclusion && form.capType !== "NONE" && (
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="label" htmlFor="rf-capmode">When the cap is reached</label>
              <select
                id="rf-capmode"
                className="input"
                value={form.capMode}
                onChange={(e) => setForm({ ...form, capMode: e.target.value })}
              >
                <option value="BONUS">Only the bonus stops — the base rate is always earned</option>
                <option value="BASE">The rest earns the base rate</option>
                <option value="NOTHING">The rest earns nothing</option>
              </select>
              <p className="mt-1 text-xs text-ink-500">
                {form.capMode === "BONUS"
                  ? "The cap counts only what this rule adds over the base rate. SmartBuy works this way."
                  : form.capMode === "NOTHING"
                    ? "The cap counts every point, and nothing is earned past it — insurance capped at 5,000 points a statement."
                    : "The cap counts every point, and spend past it falls back to the base rate."}
              </p>
            </div>
            <div>
              <label className="label" htmlFor="rf-capgroup">Shared cap name (optional)</label>
              <input
                id="rf-capgroup"
                className="input"
                value={form.capGroup}
                onChange={(e) => setForm({ ...form, capGroup: e.target.value })}
                placeholder="e.g. SmartBuy"
              />
              <p className="mt-1 text-xs text-ink-500">
                Rules on this card with the same name share one cap — flights and hotels drawing on one 15,000.
                Saving updates the others to this cap.
              </p>
            </div>
            <div>
              <label className="label" htmlFor="rf-subcap">Its own cap inside it (optional)</label>
              <input
                id="rf-subcap"
                className="input tnum"
                type="number"
                min={0}
                value={form.subCapValue}
                onChange={(e) => setForm({ ...form, subCapValue: e.target.value })}
                placeholder="No sub-cap"
              />
              <p className="mt-1 text-xs text-ink-500">
                A smaller limit this rule may take, in the same units and period — SmartBuy vouchers' 3,000 bonus
                points within the 15,000. Spend earns only while both have room, and counts against both.
              </p>
            </div>
            <div>
              <label className="label" htmlFor="rf-subgroup">Sub-cap name (optional)</label>
              <input
                id="rf-subgroup"
                className="input"
                disabled={!Number(form.subCapValue)}
                value={form.subCapGroup}
                onChange={(e) => setForm({ ...form, subCapGroup: e.target.value })}
                placeholder="e.g. SmartBuy vouchers"
              />
              <p className="mt-1 text-xs text-ink-500">Rules naming the same sub-cap share it — Gyftr and Woohoo vouchers on one 3,000.</p>
            </div>
            <div>
              <label className="label" htmlFor="rf-perday">And no more than, per day (optional)</label>
              <input
                id="rf-perday"
                className="input tnum"
                type="number"
                min={0}
                value={form.capPerDay}
                onChange={(e) => setForm({ ...form, capPerDay: e.target.value })}
                placeholder="No daily limit"
              />
              <p className="mt-1 text-xs text-ink-500">
                A day's limit on the same cap, alongside the period's — SmartBuy on Regalia: 4,000 a month, 2,000 a day.
              </p>
            </div>
          </div>
        )}

        {!form.isExclusion && (
          <div className="rounded-lg border border-ink-200 p-3">
            <h3 className="label mb-2">Only past a spend threshold (optional)</h3>
            <div className="grid gap-4 sm:grid-cols-3">
              <div>
                <label className="label" htmlFor="rf-qspend">Once spend reaches (₹)</label>
                <input
                  id="rf-qspend"
                  className="input tnum"
                  type="number"
                  min={0}
                  value={form.qualifyingSpend}
                  onChange={(e) => setForm({ ...form, qualifyingSpend: e.target.value })}
                  placeholder="Always applies"
                />
              </div>
              <div>
                <label className="label" htmlFor="rf-qperiod">In each</label>
                <select
                  id="rf-qperiod"
                  className="input"
                  disabled={!Number(form.qualifyingSpend)}
                  value={form.qualifyingPeriod}
                  onChange={(e) => setForm({ ...form, qualifyingPeriod: e.target.value })}
                >
                  {capPeriods.map((p) => (
                    <option key={p} value={p}>
                      {p === "STATEMENT" ? "Statement cycle" : p.charAt(0) + p.slice(1).toLowerCase()}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label" htmlFor="rf-qmode">Then it pays on</label>
                <select
                  id="rf-qmode"
                  className="input"
                  disabled={!Number(form.qualifyingSpend)}
                  value={form.qualifyingMode}
                  onChange={(e) => setForm({ ...form, qualifyingMode: e.target.value })}
                >
                  <option value="ALL">All of that period's spend</option>
                  <option value="ABOVE">Only spend past the threshold</option>
                </select>
              </div>
            </div>
            <p className="mt-2 text-xs text-ink-500">
              {form.qualifyingMode === "ABOVE"
                ? "Only what you spend past the threshold earns this rule — Magnus's 35 per ₹200 above ₹1.5L a month."
                : "Once the period's spend reaches it, every matching spend in the period earns this rule, the ones before it too — BizBlack's 5X after ₹50,000 a cycle."}{" "}
              Spend counts toward it unless it is excluded or another accelerated rule already rewards it.
            </p>
            <div className="mt-3 max-w-xs">
              <label className="label" htmlFor="rf-bonusdays">Bonus credited separately, days after the period</label>
              <input
                id="rf-bonusdays"
                className="input tnum"
                type="number"
                min={0}
                step={1}
                max={365}
                value={form.bonusCreditDays}
                onChange={(e) => setForm({ ...form, bonusCreditDays: e.target.value })}
                placeholder="With the base points"
              />
              <p className="mt-1 text-xs text-ink-500">
                For a bank that credits the extra over the base later — Axis, 45 days after the month. Counted
                from the statement date for a statement-cycle rule, so 0 is "with the statement", as BizBlack does.
              </p>
            </div>
            {form.bonusCreditDays.trim() !== "" && (
              <div className="mt-3 grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="label" htmlFor="rf-basis">Counted from</label>
                  <select
                    id="rf-basis"
                    className="input"
                    value={form.bonusCreditBasis}
                    onChange={(e) => setForm({ ...form, bonusCreditBasis: e.target.value })}
                  >
                    <option value="PERIOD_END">The end of the period</option>
                    <option value="TRANSACTION">The day of the spend</option>
                  </select>
                  <label className="mt-1.5 flex items-center gap-2 text-xs text-ink-600">
                    <input
                      type="checkbox"
                      className="h-3.5 w-3.5 rounded border-ink-300 text-accent-600 focus:ring-accent-500"
                      checked={form.bonusCreditWorkingDays}
                      onChange={(e) => setForm({ ...form, bonusCreditWorkingDays: e.target.checked })}
                    />
                    Working days (Monday to Friday) — SmartBuy's 90
                  </label>
                </div>
                <div>
                  <p className="label">Part of it sooner (optional)</p>
                  <div className="flex items-center gap-2 text-xs text-ink-600">
                    <input
                      aria-label="Early instalment, as a multiple of the base rate"
                      className="input tnum w-20"
                      type="number"
                      min={0}
                      step="0.5"
                      value={form.bonusEarlyMultiple}
                      onChange={(e) => setForm({ ...form, bonusEarlyMultiple: e.target.value })}
                      placeholder="4"
                    />
                    X the base, within
                    <input
                      aria-label="Days after the spend for the early instalment"
                      className="input tnum w-20"
                      type="number"
                      min={0}
                step={1}
                      value={form.bonusEarlyDays}
                      onChange={(e) => setForm({ ...form, bonusEarlyDays: e.target.value })}
                      placeholder="7"
                    />
                    days of the spend
                  </div>
                  <p className="mt-1 text-xs text-ink-500">SmartBuy Duty Free: 5X within 7 working days — 4X over the base — and the rest later.</p>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="rounded-lg border border-ink-200 p-3">
          <h3 className="label mb-2">When this rule applies</h3>
          {state.rule && (
            <label className="mb-3 flex items-start gap-2 text-sm text-ink-700">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 rounded border-ink-300 text-accent-600 focus:ring-accent-500"
                checked={changeFrom !== null}
                onChange={(e) => setChangeFrom(e.target.checked ? form.validFrom || new Date().toISOString().slice(0, 10) : null)}
              />
              <span>
                These changes start on a date — the bank changed its terms
                <span className="block text-xs text-ink-500">
                  Spends before it keep the rule as it was, even when points are recalculated. Leave this off to
                  correct the rule for every date.
                </span>
              </span>
            </label>
          )}
          {changeFrom !== null ? (
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="label" htmlFor="rf-changefrom">New terms start on</label>
                <input id="rf-changefrom" className="input" type="date" required value={changeFrom} onChange={(e) => setChangeFrom(e.target.value)} />
              </div>
              <div>
                <label className="label" htmlFor="rf-until2">Until (optional)</label>
                <input id="rf-until2" className="input" type="date" value={form.validUntil} onChange={(e) => setForm({ ...form, validUntil: e.target.value })} />
              </div>
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="label" htmlFor="rf-from">In force from (optional)</label>
                <input id="rf-from" className="input" type="date" value={form.validFrom} onChange={(e) => setForm({ ...form, validFrom: e.target.value })} />
              </div>
              <div>
                <label className="label" htmlFor="rf-until">Until, inclusive (optional)</label>
                <input id="rf-until" className="input" type="date" value={form.validUntil} onChange={(e) => setForm({ ...form, validUntil: e.target.value })} />
              </div>
              <p className="text-xs text-ink-500 sm:col-span-2">
                Leave both empty for a rule that always applies. A dated rule suits an offer — 20X for a festive week.
              </p>
            </div>
          )}
        </div>

        <div>
          <label className="label" htmlFor="rf-notes">Notes</label>
          <input id="rf-notes" className="input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
        </div>

        {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn-primary" disabled={busy}>{busy ? "Saving…" : "Save rule"}</button>
        </div>
      </form>
    </Modal>
  );
}

/** The rule form's one "when the cap is reached" answer, from the two stored fields. */
function capModeOf(rule: EarnRule | null | undefined): "BONUS" | "BASE" | "NOTHING" {
  if (rule?.capAppliesTo === "BONUS") return "BONUS";
  return rule?.capOverflow === "NOTHING" ? "NOTHING" : "BASE";
}

/** A rule's dates are whole days in UTC, the way they are stored. */
function ruleDay(iso: string): string {
  return new Date(`${iso.slice(0, 10)}T00:00:00Z`).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" });
}

function validityLabel(rule: EarnRule): string {
  if (rule.validFrom && rule.validUntil) return `${ruleDay(rule.validFrom)} – ${ruleDay(rule.validUntil)}`;
  if (rule.validFrom) return `From ${ruleDay(rule.validFrom)}`;
  return `Until ${ruleDay(rule.validUntil as string)}`;
}

/** Whether a rule applies today, has ended, or is still to start. */
function ruleState(rule: EarnRule): "ACTIVE" | "ENDED" | "UPCOMING" {
  const today = new Date().toISOString().slice(0, 10);
  if (rule.validUntil && rule.validUntil.slice(0, 10) < today) return "ENDED";
  if (rule.validFrom && rule.validFrom.slice(0, 10) > today) return "UPCOMING";
  return "ACTIVE";
}

/**
 * A fixed rate read back against the card's base, so a bonus entered where
 * the total belongs shows up at once: 45 per ₹150 on a 5-per-₹150 card reads
 * as 9X, not the 10X meant.
 */
function RateReading({
  form,
  base,
  isBaseRule,
}: {
  form: { rate: string; unitAmount: string };
  base: EarnRule | null;
  isBaseRule: boolean;
}) {
  const unitAmount = Number(form.unitAmount);
  const reading = base && unitAmount > 0 ? describeRate({ rate: Number(form.rate), unitAmount }, base) : null;

  if (isBaseRule) {
    return (
      <p className="text-xs text-ink-500 sm:col-span-3">
        What every spend earns unless a more specific rule matches. Rules set as a multiple follow this rate — change
        it, and they change with it.
      </p>
    );
  }
  return (
    <div className="rounded-lg bg-ink-50 p-3 text-xs text-ink-600 sm:col-span-3">
      Enter the <strong>total</strong> this rule earns, not the extra: the matching rule is used <em>instead of</em> the
      base rate, not on top of it.
      {reading && <span className="mt-1 block font-medium text-ink-800">= {reading}</span>}
    </div>
  );
}

/**
 * A rule set as a multiple of the card's base rate — how issuers state it,
 * "10X on hotels" — with whether the multiple counts the base or sits on top.
 */
function MultiplierFields({
  multiplier,
  includesBase,
  base,
  onChange,
}: {
  multiplier: string;
  includesBase: boolean;
  base: EarnRule | null;
  onChange: (multiplier: string, includesBase: boolean) => void;
}) {
  const m = Number(multiplier);
  const total = multiplier !== "" && m > 0 ? (includesBase ? m : m + 1) : null;
  return (
    <div>
      <div className="flex flex-wrap items-center gap-2">
        <label htmlFor="rf-multiplier" className="sr-only">Multiple of the base rate</label>
        <input
          id="rf-multiplier"
          className="input tnum w-24"
          type="number"
          min={0}
          step="0.5"
          required
          placeholder="10"
          value={multiplier}
          onChange={(e) => onChange(e.target.value, includesBase)}
        />
        <span className="text-sm text-ink-700">X</span>
        <label htmlFor="rf-includes" className="sr-only">Whether the multiple includes the base rate</label>
        <select
          id="rf-includes"
          className="input w-auto"
          value={includesBase ? "INCL" : "ONTOP"}
          onChange={(e) => onChange(multiplier, e.target.value === "INCL")}
        >
          <option value="INCL">including the base rate</option>
          <option value="ONTOP">on top of the base rate</option>
        </select>
      </div>
      {base ? (
        total !== null && (
          <p className="mt-2 text-xs text-ink-600">
            ={" "}
            <span className="font-medium text-ink-800">
              {describeRate({ rate: Math.round(base.rate * total * 100) / 100, unitAmount: base.unitAmount }, base)}
            </span>{" "}
            That is {Math.round(base.rate * total * 100) / 100} points per ₹{base.unitAmount} today, and it follows the
            base rate if the base rate changes.
          </p>
        )
      ) : (
        <p className="mt-2 text-xs text-amber-800">
          This card has no base rate yet, so there is nothing to multiply. Set it with <strong>Edit</strong> on the
          card, or choose a fixed rate.
        </p>
      )}
      <p className="mt-1 text-xs text-ink-500">
        {includesBase
          ? "10X including the base on a card earning 5 per ₹150 is 50 per ₹150: 5 base + 45 bonus."
          : "+9X on top of the base on a card earning 5 per ₹150 is also 50 per ₹150: 5 base + 45 bonus."}
      </p>
    </div>
  );
}
